import type { PageRef } from '@/types'
import { getSource } from './sources'

const cache = new Map<string, Promise<string>>()
let active = 0
const queue: (() => void)[] = []

function schedule<T>(job: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const run = () => {
      active++
      job()
        .then(resolve, reject)
        .finally(() => {
          active--
          queue.shift()?.()
        })
    }
    if (active < 2) run()
    else queue.push(run)
  })
}

/** Render a page thumbnail (base orientation) and return an object URL. Cached. */
export function getThumb(page: PageRef, cssWidth: number): Promise<string> {
  if (!page.srcId) return Promise.resolve('')
  const width = Math.ceil((cssWidth * Math.min(2, window.devicePixelRatio || 1)) / 40) * 40
  const key = `${page.srcId}:${page.srcIndex}:${width}`
  let hit = cache.get(key)
  if (!hit) {
    hit = schedule(async () => {
      const src = getSource(page.srcId)
      if (!src) return ''
      const p = await src.pdf.getPage(page.srcIndex + 1)
      const vp = p.getViewport({ scale: width / page.width, rotation: page.baseRotation })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(vp.width)
      canvas.height = Math.ceil(vp.height)
      await p.render({ canvas, viewport: vp, annotationMode: 1 }).promise
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.85))
      canvas.width = 0
      return blob ? URL.createObjectURL(blob) : ''
    })
    cache.set(key, hit)
    hit.catch(() => cache.delete(key))
  }
  return hit
}

/** Small JPEG data URL for the recent-files list. */
export async function makeCoverThumb(page: PageRef, width = 180): Promise<string | undefined> {
  const src = getSource(page.srcId)
  if (!src) return undefined
  const p = await src.pdf.getPage(page.srcIndex + 1)
  const vp = p.getViewport({ scale: width / page.width, rotation: page.baseRotation + page.rotation })
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(vp.width)
  canvas.height = Math.ceil(vp.height)
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  await p.render({ canvas, viewport: vp }).promise
  const url = canvas.toDataURL('image/jpeg', 0.72)
  canvas.width = 0
  return url
}

export function clearThumbs() {
  for (const p of cache.values()) p.then((u) => u && URL.revokeObjectURL(u)).catch(() => {})
  cache.clear()
}
